import { ng } from "../ngds";

// Depends on ngds alone (no protoGen/funlib, which pull in .tsx), so a Node service such as the
// sx-center print module can import it as it is.

// Self-weight and mass of a beam element, from one source of truth.
//
// The self-weight line load an app applies to the analysis and the element weight it prints must be
// the SAME number, or the printout discredits the analysis. So there is one calculation here: the
// line load, piecewise linear along the element. The mass is only ever derived from it (∫q dx / g),
// never computed separately.
//
// Quantities follow the carbon take-off in WS_PRE_Stress (carbon_footprint.cpp): bars from the
// standalone bar end points (sx-column) or from the groups and layers that generate them (sx-beam),
// counted as BarsInLayer does, link count floor(length / s) + 1, and link length from
// LinkCentreLineLength (closed rectangle on the section bounding box inset by cover + dia/2, plus two
// hooks of max(10 dia, 70 mm)). Keep the two in step. Unlike the carbon volume, which is gross on
// purpose, the concrete here is NET of the steel it displaces: this is a mass, and every kilogram
// is counted once.

/** Gravitational acceleration [m/s²] used to turn mass into self-weight and back. */
export const GRAVITY = 9.81;

/** Density used when a reinforcement material carries none [kg/m³] (as the carbon take-off). */
const DEFAULT_STEEL_DENSITY = 7850;

/**
 * The databases a calculation looks materials and sections up in. Only these two are read, so a
 * caller holding the project as JSON can pass it as it is instead of converting the whole input.
 */
export type ElementDbs = Pick<ng.input.IData, "mtrlDb" | "secDb">;

/** One trapezoidal piece of the self-weight line load: positions along the element [m], values [N/m]. */
export interface SelfWeightPiece {
  start: number;
  end: number;
  qStart: number;
  qEnd: number;
}

/**
 * The density [kg/m³] an element of this material weighs with.
 *
 * - concrete: `conData.properties.density` — plain concrete; the reinforcement is added separately.
 * - steel: `steelData.properties.density`.
 * - timber: the MEAN density. `solidData.density` is ρ_k, a 5 % fractile, which understates the
 *   weight by some 15-20 % (C24: 350 against 420). Board products carry no mean, so ρ_k it is.
 * - reinforcement: `reinforcementData.properties.density`, 7850 when missing.
 */
export function elementDensity(mtrl: ng.material.Data | null | undefined): number {
  if (!mtrl) return 0;
  switch (mtrl.type) {
    case ng.material.Mtrl.MTRL_CONCRETE:
      return mtrl.getConData()?.properties?.density || 0;
    case ng.material.Mtrl.MTRL_STEEL:
      return mtrl.getSteelData()?.properties?.density || 0;
    case ng.material.Mtrl.MTRL_TIMBER: {
      const timber = mtrl.getTimberData();
      const solid = timber?.getSolidData();
      if (solid) return solid.meanDensity || solid.density || 0;
      return timber?.getBoardData()?.density || 0;
    }
    case ng.material.Mtrl.MTRL_REINFORCEMENT:
      return mtrl.getReinforcementData()?.properties?.density || DEFAULT_STEEL_DENSITY;
    default:
      return 0;
  }
}

const findMaterial = (guid: string | undefined, input: ElementDbs): ng.material.Data | null => {
  const raw = guid ? (input.mtrlDb ?? []).find((m) => m.id?.guid === guid) : null;
  if (!raw) return null;
  // fromObject refuses an object that is already an instance (it wants JSON), so pass those through.
  return raw instanceof ng.material.Data ? raw : ng.material.Data.fromObject(raw);
};

const findSection = (guid: string | undefined, input: ElementDbs): ng.sections.Section | null => {
  const raw = guid ? (input.secDb ?? []).find((s) => s.id?.guid === guid) : null;
  if (!raw) return null;
  return raw instanceof ng.sections.Section ? raw : ng.sections.Section.fromObject(raw);
};

// Shoelace area of a closed polyline [m²].
const polylineArea = (poly: ng.geometry.PolyLine2D | null | undefined): number => {
  const pts = poly?.points ?? [];
  if (pts.length < 3) return 0;
  let twice = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    const q = pts[(i + 1) % pts.length];
    twice += p.x * q.y - q.x * p.y;
  }
  return Math.abs(twice) / 2;
};

// Cross-section area [m²]: `units.a`, or the perimeter less its holes when that is absent.
const sectionArea = (sec: ng.sections.Section | null): number => {
  if (!sec) return 0;
  if ((sec.units?.a ?? 0) > 0) return sec.units!.a;
  const area =
    polylineArea(sec.polylines?.perimeter) -
    (sec.polylines?.holes ?? []).reduce((sum, hole) => sum + polylineArea(hole), 0);
  return Math.max(area, 0);
};

// Outer width (sectional y) and height (sectional z) [m] from the perimeter polyline.
const sectionOuterSize = (sec: ng.sections.Section | null): { width: number; height: number } => {
  const pts = sec?.polylines?.perimeter?.points ?? [];
  if (pts.length < 3) return { width: 0, height: 0 };
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  return { width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys) };
};

// Concrete covers [m], per side. `CoverAndSpace.cover` is in mm.
const elementCovers = (concrete: ng.beam.ConcreteElement) => {
  const covers = { top: 0, btm: 0, lft: 0, rgt: 0 };
  for (const dist of concrete.distances ?? []) {
    const cover = (dist.cover ?? 0) * 1.0e-3;
    switch (dist.side) {
      case ng.design.concrete.BeamSide.BEAM_SIDE_TOP:
        covers.top = cover;
        break;
      case ng.design.concrete.BeamSide.BEAM_SIDE_BOTTOM:
        covers.btm = cover;
        break;
      case ng.design.concrete.BeamSide.BEAM_SIDE_LEFT:
        covers.lft = cover;
        break;
      case ng.design.concrete.BeamSide.BEAM_SIDE_RIGHT:
        covers.rgt = cover;
        break;
    }
  }
  return covers;
};

// Centre-line length of one link [m] — mirrors LinkCentreLineLength in WS_PRE_Stress. Note that
// `ng.link.Group.diameter` is in metres, unlike bar diameters, which are in millimetres.
const linkCentreLineLength = (
  grp: ng.link.Group,
  width: number,
  height: number,
  covers: ReturnType<typeof elementCovers>,
): number => {
  const dia = grp.diameter;
  const legHor = width - covers.lft - covers.rgt - dia;
  let legVert = height - covers.top - covers.btm - dia;
  if (legHor <= 0 || legVert <= 0) return 0;

  const angle = grp.angle > 0 ? grp.angle : 90;
  const sinAngle = Math.abs(Math.sin((angle * Math.PI) / 180));
  if (sinAngle > 1.0e-6) legVert /= sinAngle;

  const legs = grp.legs > 0 ? grp.legs : 2;
  const stirrupsPerLink = Math.max(1, legs / 2);
  const hooks = 2 * Math.max(10 * dia, 0.07);
  return stirrupsPerLink * (2 * (legHor + legVert) + hooks);
};

/** A contribution to the line mass [kg/m], constant over [start, end) along the element. */
interface Band {
  start: number;
  end: number;
  massPerLength: number;
}

// Steel area of one bar or wire [m²] for a nominal diameter [m] — mirrors BarSteelArea in
// WS_PRE_Stress: a 7-wire strand fills 0.76 of its circumscribed circle.
const barSteelArea = (mtrl: ng.material.Data | null, dia: number): number => {
  const gross = (Math.PI * dia * dia) / 4;
  const reinf = mtrl?.getReinforcementData();
  return reinf?.getWireProfile() === ng.reinf.WireProfile.WIRE_PROFILE_K7 ? 0.76 * gross : gross;
};

// Number of bars or strands in a layer — mirrors BarsInLayer in WS_PRE_Stress, which follows the
// generation routines: a rebar layer never holds fewer than two bars and loses two link diameters
// and gains a 0.5 mm rounding pad when given by a spacing; a strand layer does neither.
const barsInLayer = (
  lay: ng.reinf.Layer,
  dia: number,
  width: number,
  covers: ReturnType<typeof elementCovers>,
  maxLinkDia: number,
  isStrand: boolean,
): number => {
  const s = lay.getS();
  if (s === null) {
    const num = lay.getNum() ?? 0;
    return isStrand ? num : Math.max(num, 2);
  }
  if (s <= 0 || width <= 0) return 0;
  const pad = isStrand ? 0 : 0.5e-3;
  const stirrups = isStrand ? 0 : 2 * maxLinkDia;
  const widthRed = width - (covers.lft + covers.rgt + stirrups + dia) + pad;
  if (widthRed <= 0) return 0;
  return Math.floor((widthRed * 1.0e3) / s) + 1;
};

// Longitudinal bars or strands of one kind, as bands — mirrors AddLongitudinalReinf in WS_PRE_Stress,
// with the same precedence: exact bar coordinates (sx-column), then explicitly listed bars, and last
// the layers and groups that generate the bars (sx-beam).
const longitudinalBands = (
  input: ElementDbs,
  standalone: ng.reinf.StandAloneBar[],
  bars: ng.reinf.Straight[],
  lays: ng.reinf.Layer[],
  grps: ng.reinf.Group[],
  geometry: { width: number; covers: ReturnType<typeof elementCovers>; maxLinkDia: number },
  isStrand: boolean,
  concreteDensity: number,
  elementLength: number,
): Band[] => {
  const steelDensityOf = (mtrl: ng.material.Data | null) => elementDensity(mtrl) || DEFAULT_STEEL_DENSITY;

  // A bar with no extent along the element (start and end both at x = 0, as projects stored before
  // sx-column normalised them carry) is a position in the section only; it runs the full element,
  // which is what normalizeStandaloneBars makes of it.
  if (standalone.length > 0) {
    const bands: Band[] = [];
    for (const bar of standalone) {
      const a = bar.startPoint;
      const b = bar.endPoint;
      if (!a || !b) continue;
      const mtrl = findMaterial(bar.mtrlGuid, input);
      const area = barSteelArea(mtrl, bar.diameter * 1.0e-3); // [mm] -> [m]
      const axial = Math.abs(b.x - a.x);
      // A sloping bar is longer than the stretch of element it covers.
      const volumePerLength = axial > 1.0e-9 ? (area * Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z)) / axial : area;
      bands.push({
        start: axial > 1.0e-9 ? Math.min(a.x, b.x) : 0,
        end: axial > 1.0e-9 ? Math.max(a.x, b.x) : elementLength,
        massPerLength: volumePerLength * (steelDensityOf(mtrl) - concreteDensity),
      });
    }
    return bands;
  }

  // One band per group, `count` bars of the group's diameter over the group's extent.
  const groupBand = (grp: ng.reinf.Group, count: number): Band | null => {
    const start = Math.max(grp.start, 0);
    const end = Math.min(grp.start + grp.length, elementLength);
    if (count <= 0 || end - start <= 1.0e-9) return null;
    const mtrl = findMaterial(grp.mtrlGuid, input);
    const area = barSteelArea(mtrl, grp.diameter * 1.0e-3); // [mm] -> [m]
    return { start, end, massPerLength: count * area * (steelDensityOf(mtrl) - concreteDensity) };
  };
  const grpOf = (grpGuid: string) => grps.find((g) => g.id?.guid === grpGuid);

  if (bars.length > 0) {
    return bars
      .map((bar) => {
        const lay = lays.find((l) => l.id?.guid === bar.layGuid);
        const grp = lay ? grpOf(lay.grpGuid) : undefined;
        return grp ? groupBand(grp, 1) : null;
      })
      .filter((b): b is Band => b !== null);
  }

  return lays
    .map((lay) => {
      const grp = grpOf(lay.grpGuid);
      if (!grp) return null;
      const count = barsInLayer(
        lay,
        grp.diameter * 1.0e-3,
        geometry.width,
        geometry.covers,
        geometry.maxLinkDia,
        isStrand,
      );
      return groupBand(grp, count);
    })
    .filter((b): b is Band => b !== null);
};

// Steel bands of a concrete element: the steel's own mass less the concrete it displaces, so that
// adding them to the gross concrete term leaves concrete net of steel.
const reinforcementBands = (
  input: ElementDbs,
  beam: ng.beam.Data,
  concreteDensity: number,
  elementLength: number,
): Band[] => {
  const concrete = beam.getConcrete();
  if (!concrete) return [];

  // Bar counts and links are referred to the start section of the first segment, as the carbon
  // take-off does.
  const firstSec = findSection(beam.segments[0]?.layer?.start?.secGuid, input);
  const { width, height } = sectionOuterSize(firstSec);
  const covers = elementCovers(concrete);
  const maxLinkDia = Math.max(0, ...(concrete.links?.linkGrps ?? []).map((g) => g.diameter));
  const geometry = { width, covers, maxLinkDia };

  const rebars = concrete.rebars;
  const strands = concrete.strands;
  const bands: Band[] = [
    ...longitudinalBands(
      input,
      rebars?.standaloneBars ?? [],
      rebars?.bars ?? [],
      rebars?.lays ?? [],
      rebars?.grps ?? [],
      geometry,
      false,
      concreteDensity,
      elementLength,
    ),
    ...longitudinalBands(
      input,
      strands?.standaloneStrands ?? [],
      strands?.strands ?? [],
      strands?.lays ?? [],
      strands?.grps ?? [],
      geometry,
      true,
      concreteDensity,
      elementLength,
    ),
  ];

  // Links, smeared over the length of their group.
  for (const grp of concrete.links?.linkGrps ?? []) {
    if (grp.s <= 0 || grp.diameter <= 0 || grp.length <= 0) continue;
    const linkLength = linkCentreLineLength(grp, width, height, covers);
    if (linkLength <= 0) continue;
    const count = Math.floor(grp.length / grp.s) + 1;
    const area = (Math.PI * grp.diameter * grp.diameter) / 4;
    const steelDensity = elementDensity(findMaterial(grp.mtrlGuid, input)) || DEFAULT_STEEL_DENSITY;
    const volume = count * linkLength * area;
    bands.push({
      start: grp.start,
      end: grp.start + grp.length,
      massPerLength: (volume / grp.length) * (steelDensity - concreteDensity),
    });
  }

  return bands;
};

/**
 * The self-weight line load of a beam element [N/m], as trapezoids along its axis from x = 0.
 *
 * Breaks at every segment end and wherever a bar or link group starts or stops, so that each piece
 * is exactly linear: the section area varies linearly within a segment and the steel is constant
 * within a piece. Empty when the element has no material density or no section area.
 */
export function selfWeightLineLoads(input: ElementDbs, beam: ng.beam.Data): SelfWeightPiece[] {
  const density = elementDensity(findMaterial(beam.mtrlGuid, input));
  if (density <= 0) return [];

  // Gross cross-section, per segment, linear between its start and end area.
  const segments: { start: number; end: number; aStart: number; aEnd: number }[] = [];
  let x = 0;
  for (const seg of beam.segments) {
    if (seg.length <= 0) continue;
    const aStart = sectionArea(findSection(seg.layer?.start?.secGuid, input));
    const endSec = findSection(seg.layer?.end?.secGuid, input);
    const aEnd = endSec ? sectionArea(endSec) : aStart;
    segments.push({ start: x, end: x + seg.length, aStart, aEnd });
    x += seg.length;
  }
  const totalLength = x;
  if (totalLength <= 0) return [];

  const bands = beam.getConcrete() ? reinforcementBands(input, beam, density, totalLength) : [];

  const breaks = new Set<number>([0, totalLength]);
  for (const s of segments) breaks.add(s.start).add(s.end);
  for (const b of bands) {
    if (b.start > 0 && b.start < totalLength) breaks.add(b.start);
    if (b.end > 0 && b.end < totalLength) breaks.add(b.end);
  }
  const xs = [...breaks].sort((p, q) => p - q);

  const pieces: SelfWeightPiece[] = [];
  for (let i = 0; i + 1 < xs.length; i++) {
    const x1 = xs[i];
    const x2 = xs[i + 1];
    if (x2 - x1 < 1.0e-9) continue;
    const mid = (x1 + x2) / 2;
    const seg = segments.find((s) => mid >= s.start && mid < s.end);
    if (!seg) continue;
    const areaAt = (px: number) => seg.aStart + ((seg.aEnd - seg.aStart) * (px - seg.start)) / (seg.end - seg.start);
    const steel = bands.reduce((sum, b) => (mid >= b.start && mid < b.end ? sum + b.massPerLength : sum), 0);
    pieces.push({
      start: x1,
      end: x2,
      qStart: (areaAt(x1) * density + steel) * GRAVITY,
      qEnd: (areaAt(x2) * density + steel) * GRAVITY,
    });
  }
  return pieces;
}

/**
 * Guid of the self-weight load case, or undefined. Goes through fromObject because a load case held
 * as JSON carries its type as the enum name ("TYPE_SELF_WEIGHT") or its number, depending on whether
 * it came off the wire or from a reducer.
 */
export const selfWeightCaseGuid = (loadcases: unknown[] | null | undefined): string | undefined =>
  (loadcases ?? []).map((lc) => ng.loadcase.Data.fromObject(lc)).find((lc) => lc.type === ng.loadcase.Type.TYPE_SELF_WEIGHT)
    ?.id?.guid;

/** One self-weight piece as the line load a project stores. `guid` is the new load's own guid. */
export const selfWeightLoadJson = (piece: SelfWeightPiece, lcGuid: string, elemGuid: string, guid: string) => ({
  id: { guid, name: "Self-weight" },
  type: ng.load.Type.TYPE_FORCE,
  distribution: ng.load.DistributionType.DISTRIBUTION_TYPE_LINE,
  direction: { x: -1, y: 0, z: 0 },
  positions: [
    { x: piece.start, y: 0, z: 0 },
    { x: piece.end, y: 0, z: 0 },
  ],
  values: [piece.qStart, piece.qEnd],
  lcaseGuid: lcGuid,
  assignedObjects: [elemGuid],
});

/**
 * The loads with the self-weight case's loads replaced by `pieces`, in the place the old ones held
 * (so a loads table does not reorder), keeping the load case and with it every combination that
 * includes it. Undefined when the loading has no self-weight case. `newGuid` makes each new load's
 * guid (it is passed in so this file keeps depending on ngds alone).
 */
export function replaceSelfWeightLoads<L extends { lcaseGuid?: string | null }>(
  loading: { loadcases?: unknown[] | null; loads?: L[] | null },
  pieces: SelfWeightPiece[],
  elemGuid: string,
  newGuid: () => string,
): (L | ReturnType<typeof selfWeightLoadJson>)[] | undefined {
  const lcGuid = selfWeightCaseGuid(loading.loadcases);
  if (!lcGuid) return undefined;
  const loads = loading.loads ?? [];
  const at = loads.findIndex((l) => l.lcaseGuid === lcGuid);
  const kept: (L | ReturnType<typeof selfWeightLoadJson>)[] = loads.filter((l) => l.lcaseGuid !== lcGuid);
  const fresh = pieces.map((p) => selfWeightLoadJson(p, lcGuid, elemGuid, newGuid()));
  kept.splice(at < 0 ? kept.length : at, 0, ...fresh);
  return kept;
}

/** Mass [kg] carried by a set of self-weight pieces. */
export function massOfSelfWeight(pieces: SelfWeightPiece[]): number {
  return pieces.reduce((sum, p) => sum + ((p.qStart + p.qEnd) / 2) * (p.end - p.start), 0) / GRAVITY;
}

/**
 * The self-weight pieces a project's loading actually holds — the line loads in its
 * `TYPE_SELF_WEIGHT` load case — or null when it has none. What the analysis was given, so a printed
 * weight derived from these cannot disagree with it.
 *
 * With `elemGuid`, only the loads assigned to that element count, so a structure of several elements
 * does not credit each with all of them. A load assigned to nothing is a legacy single-element load
 * and counts for any element; `structureMass` avoids relying on that when there is more than one.
 */
export function storedSelfWeight(
  loading: ng.loading.Data | null | undefined,
  elemGuid?: string,
): SelfWeightPiece[] | null {
  const lcase = loading?.loadcases.find((lc) => lc.type === ng.loadcase.Type.TYPE_SELF_WEIGHT);
  if (!lcase) return null;
  const guid = lcase.id?.guid;
  return (loading?.loads ?? [])
    .filter((l) => l.lcaseGuid === guid && l.positions.length >= 2 && l.values.length >= 2)
    .filter((l) => !elemGuid || l.assignedObjects.length === 0 || l.assignedObjects.includes(elemGuid))
    .map((l) => {
      // Measured along the load's own line: projects stored before the element axis was x carry
      // their self-weight along z.
      const [a, b] = l.positions;
      const start = a.x;
      return {
        start,
        end: start + Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z),
        qStart: l.values[0],
        qEnd: l.values[1],
      };
    });
}

/**
 * The element's mass [kg] as an app should report it: integrated from the self-weight the project
 * holds, which is what the analysis was given, and calculated only when it holds none. Every place
 * that shows a weight uses this, so none of them can disagree with the analysis or with each other.
 */
export function elementMass(
  input: ElementDbs,
  beam: ng.beam.Data,
  loading: ng.loading.Data | null | undefined,
  elemGuid?: string,
): number {
  const stored = storedSelfWeight(loading, elemGuid);
  return massOfSelfWeight(stored?.length ? stored : selfWeightLineLoads(input, beam));
}

/**
 * The mass [kg] of every beam element of the input, summed — what an app with more than one element
 * (or more than one span) reports as the weight. Each element is weighed as `elementMass` does. When a
 * structure has several elements and some of its self-weight loads are assigned to none of them, the
 * stored loads cannot be told apart, so that structure's elements are calculated instead.
 */
export function structureMass(input: ng.input.Data): number {
  let mass = 0;
  for (const structure of input.structures) {
    const beams = structure.elements.filter((e) => e.getBeam());
    const swGuid = structure.loading?.loadcases.find((lc) => lc.type === ng.loadcase.Type.TYPE_SELF_WEIGHT)?.id?.guid;
    const ambiguous =
      beams.length > 1 &&
      (structure.loading?.loads ?? []).some((l) => l.lcaseGuid === swGuid && l.assignedObjects.length === 0);
    for (const element of beams) {
      mass += ambiguous
        ? massOfSelfWeight(selfWeightLineLoads(input, element.getBeam()!))
        : elementMass(input, element.getBeam()!, structure.loading, element.id?.guid);
    }
  }
  return mass;
}
